import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { articleData, defaultArticleData } from '@/mocks/article';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? articleData[id] : null;
  const data = article || defaultArticleData;
  const [showReplyBox, setShowReplyBox] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');

  const submitReply = (commentId: string) => {
    if (!replyText.trim()) return;
    setReplyText('');
    setShowReplyBox(null);
  };

  useEffect(() => {
    document.title = `${data.title} - 黑料在线`;
  }, [data.title]);

  useEffect(() => {
    const baseUrl = window.location.origin + __BASE_PATH__;
    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        '@id': `${baseUrl}/article/${data.id}`,
        headline: data.title,
        description: data.content[0]?.slice(0, 150),
        image: data.heroImage,
        datePublished: data.date,
        author: {
          '@type': 'Organization',
          name: '黑料在线编辑部',
          url: baseUrl
        },
        publisher: {
          '@type': 'Organization',
          name: '黑料在线',
          url: baseUrl,
          logo: { '@type': 'ImageObject', url: `${baseUrl}/vite.svg` }
        },
        articleSection: data.category,
        mainEntityOfPage: { '@type': 'WebPage', '@id': `${baseUrl}/article/${data.id}` }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${baseUrl}/article/${data.id}`,
        name: data.title,
        url: `${baseUrl}/article/${data.id}`,
        isPartOf: { '@id': `${baseUrl}` }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: `${baseUrl}/` },
          { '@type': 'ListItem', position: 2, name: data.category, item: `${baseUrl}/category/wanghong-baoliao` },
          { '@type': 'ListItem', position: 3, name: data.title.slice(0, 30), item: `${baseUrl}/article/${data.id}` }
        ]
      }
    ];
    const scripts: HTMLScriptElement[] = [];
    schemas.forEach((s) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(s);
      document.head.appendChild(script);
      scripts.push(script);
    });
    return () => { scripts.forEach((s) => document.head.removeChild(s)); };
  }, [data.title, data.id, data.category, data.heroImage, data.date, data.content]);

  return (
    <>
      <Navbar />
      <main>
        <article className="bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-8">
            <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line w-3.5 h-3.5 flex items-center justify-center"></i>
              <Link to={`/category/wanghong-baoliao`} className="hover:text-primary-600 transition-colors cursor-pointer">{data.category}</Link>
              <i className="ri-arrow-right-s-line w-3.5 h-3.5 flex items-center justify-center"></i>
              <span className="text-foreground-600">{data.title.slice(0, 15)}...</span>
            </nav>

            <div className="flex flex-col lg:flex-row gap-10">
              <div className="lg:w-3/5">
                <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-4 whitespace-nowrap">
                  {data.category}
                </span>
                <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 leading-tight">
                  {data.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 mt-5 text-sm text-foreground-500">
                  <div className="flex items-center gap-2">
                    <img src={data.author.avatar} alt={data.author.name} className="w-7 h-7 rounded-full object-cover" />
                    <span className="font-medium text-foreground-800">{data.author.name}</span>
                    <span className="text-foreground-400">· {data.author.role}</span>
                  </div>
                  <span>{data.date}</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line w-3.5 h-3.5 flex items-center justify-center"></i>
                    {data.views}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-chat-3-line w-3.5 h-3.5 flex items-center justify-center"></i>
                    {data.comments}
                  </span>
                </div>

                <div className="mt-8 space-y-6">
                  {data.content.map((paragraph, idx) => (
                    <p key={idx} className="text-base leading-relaxed text-foreground-800">
                      {paragraph}
                    </p>
                  ))}
                </div>

                <div className="mt-10 flex items-center gap-4">
                  <span className="text-sm text-foreground-500">分享到：</span>
                  <button className="w-9 h-9 rounded-full bg-background-100 hover:bg-primary-100 flex items-center justify-center cursor-pointer transition-colors">
                    <i className="ri-weibo-line text-foreground-700 text-sm w-4 h-4 flex items-center justify-center"></i>
                  </button>
                  <button className="w-9 h-9 rounded-full bg-background-100 hover:bg-primary-100 flex items-center justify-center cursor-pointer transition-colors">
                    <i className="ri-wechat-line text-foreground-700 text-sm w-4 h-4 flex items-center justify-center"></i>
                  </button>
                  <button className="w-9 h-9 rounded-full bg-background-100 hover:bg-primary-100 flex items-center justify-center cursor-pointer transition-colors">
                    <i className="ri-link text-foreground-700 text-sm w-4 h-4 flex items-center justify-center"></i>
                  </button>
                </div>

                <div className="mt-10 border-t border-background-200 pt-8">
                  <h3 className="text-lg font-heading font-bold text-foreground-950 mb-6">
                    评论 <span className="text-foreground-400 text-sm font-normal">({data.articleComments.length}条)</span>
                  </h3>
                  <div className="space-y-5">
                    {data.articleComments.map((comment) => (
                      <div key={comment.id} className="bg-background-100 rounded-xl p-4">
                        <div className="flex items-start gap-3">
                          <img src={comment.avatar} alt={comment.name} className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-foreground-900">{comment.name}</span>
                              <span className="text-xs text-foreground-400">{comment.date}</span>
                            </div>
                            <p className="text-sm text-foreground-700 mt-1.5 leading-relaxed">{comment.content}</p>
                            <div className="flex items-center gap-4 mt-3">
                              <button className="flex items-center gap-1 text-xs text-foreground-400 hover:text-primary-600 cursor-pointer transition-colors">
                                <i className="ri-thumb-up-line w-3.5 h-3.5 flex items-center justify-center"></i>
                                {comment.likes}
                              </button>
                              <button
                                onClick={() => setShowReplyBox(showReplyBox === comment.id ? null : comment.id)}
                                className="flex items-center gap-1 text-xs text-foreground-400 hover:text-primary-600 cursor-pointer transition-colors"
                              >
                                <i className="ri-chat-1-line w-3.5 h-3.5 flex items-center justify-center"></i>
                                回复
                              </button>
                            </div>
                            {showReplyBox === comment.id && (
                              <div className="mt-3 flex gap-2">
                                <input
                                  type="text"
                                  value={replyText}
                                  onChange={(e) => setReplyText(e.target.value)}
                                  placeholder={`回复 ${comment.name}...`}
                                  className="flex-1 bg-background-200 border border-background-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-primary-400 transition-colors"
                                />
                                <button
                                  onClick={() => submitReply(comment.id)}
                                  className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium whitespace-nowrap hover:bg-primary-600 cursor-pointer transition-colors"
                                >
                                  发送
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                        {comment.replies && comment.replies.length > 0 && (
                          <div className="mt-3 ml-12 space-y-3">
                            {comment.replies.map((reply) => (
                              <div key={reply.id} className="bg-background-200/70 rounded-lg p-3">
                                <div className="flex items-start gap-2.5">
                                  <img src={reply.avatar} alt={reply.name} className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs font-semibold text-foreground-900">{reply.name}</span>
                                      <span className="text-xs text-foreground-400">{reply.date}</span>
                                    </div>
                                    <p className="text-xs text-foreground-600 mt-1 leading-relaxed">{reply.content}</p>
                                    <div className="flex items-center gap-3 mt-2">
                                      <button className="flex items-center gap-1 text-xs text-foreground-400 hover:text-primary-600 cursor-pointer transition-colors">
                                        <i className="ri-thumb-up-line w-3 h-3 flex items-center justify-center"></i>
                                        {reply.likes}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="lg:w-2/5">
                <div className="sticky top-24 space-y-6">
                  <div className="rounded-xl overflow-hidden">
                    <img
                      src={data.heroImage}
                      alt={data.title}
                      className="w-full h-72 md:h-80 object-cover"
                    />
                  </div>

                  {data.relatedArticles.length > 0 && (
                    <div>
                      <h3 className="text-base font-heading font-bold text-foreground-950 mb-4">
                        相关爆料
                      </h3>
                      <div className="space-y-3">
                        {data.relatedArticles.map((rel) => (
                          <Link
                            key={rel.id}
                            to={`/article/${rel.id}`}
                            className="flex gap-3 p-3 rounded-lg bg-background-100 hover:bg-background-200/70 transition-colors cursor-pointer"
                          >
                            <div className="w-20 h-16 rounded-lg overflow-hidden flex-shrink-0">
                              <img src={rel.imageUrl} alt={rel.title} className="w-full h-full object-cover" />
                            </div>
                            <div className="flex-1 min-w-0 flex flex-col justify-center">
                              <span className="text-xs text-accent-600 font-medium">{rel.category}</span>
                              <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 leading-snug mt-0.5">{rel.title}</h4>
                              <div className="flex items-center gap-3 mt-1 text-xs text-foreground-400">
                                <span>{rel.date}</span>
                                <span className="flex items-center gap-0.5">
                                  <i className="ri-eye-line w-3 h-3 flex items-center justify-center"></i>
                                  {rel.views}
                                </span>
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="bg-background-100 rounded-xl p-5">
                    <h3 className="text-base font-heading font-bold text-foreground-950 mb-2">
                      订阅每日推送
                    </h3>
                    <p className="text-sm text-foreground-500 mb-3">
                      第一时间获取最新黑料爆料
                    </p>
                    <form className="flex" data-readdy-form="">
                      <input
                        type="email"
                        name="email"
                        placeholder="输入邮箱"
                        className="flex-1 bg-background-200 border border-background-300 rounded-l-lg px-3 py-2 text-sm outline-none focus:border-primary-400 transition-colors"
                      />
                      <button
                        type="submit"
                        className="bg-primary-500 hover:bg-primary-600 text-white px-4 rounded-r-lg text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
                      >
                        订阅
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}